import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PriceInput } from "@/components/PriceInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeftRight, Plus, Search, Trash2, Truck, PackageCheck, CheckCircle2, XCircle, Clock,
  ChevronDown, AlertTriangle, MapPin, ShoppingCart, PackagePlus, Store, Warehouse, History,
  Printer, Archive, FileText, User, Phone, Mail, Upload, X, Loader2, BarChart3, Hash, Send, MessageCircle,
} from "lucide-react";
import DispatchDialog from "@/components/DispatchDialog";
import SupplierSummary from "@/components/SupplierSummary";
import SupplierScoreBadge from "@/components/SupplierScoreBadge";
import Pagination from "@/components/Pagination";
import { fmtTL } from "@/components/StatusBadge";
import { curSym } from "@/lib/company";
import { useSort } from "@/lib/sort";
import { SortTh } from "@/components/SortTh";
import { useTablePrefs } from "@/lib/useTablePrefs";
import { ColumnToggle } from "@/components/ColumnToggle";
import ApprovalDialog from "@/components/ApprovalDialog";
import { printBlankTemplate } from "@/lib/templates";
import { TYPE_FILTERS, SHIPMENT_METHODS, SHIP_LABEL, DOC_TYPES, docFileHref, DocSummary, StatCard } from "@/components/orders/shared";
import TransferTimeline from "@/components/orders/TransferTimeline";
import AuditTrailPanel from "@/components/orders/AuditTrailPanel";

const STATUS = {
  requested: { label: "Onay Bekliyor", cls: "bg-amber-100 text-amber-700", icon: Clock },
  approved: { label: "Onaylı — Hazırlık/Sevk Bekliyor", cls: "bg-blue-100 text-blue-700", icon: CheckCircle2 },
  in_transit: { label: "Yolda / Sevk Edildi", cls: "bg-indigo-100 text-indigo-700", icon: Truck },
  completed: { label: "Mal Kabulü Yapıldı", cls: "bg-emerald-100 text-emerald-700", icon: PackageCheck },
  rejected: { label: "Reddedildi", cls: "bg-red-100 text-red-600", icon: XCircle },
  cancelled: { label: "İptal", cls: "bg-slate-100 text-slate-500", icon: XCircle },
};

const OTYPE = {
  transfer: { label: "Transfer", cls: "bg-slate-100 text-slate-600", icon: ArrowLeftRight },
  internal_order: { label: "İç İkmal Siparişi", cls: "bg-sky-100 text-sky-700", icon: PackagePlus },
  purchase_order: { label: "Satın Alma Siparişi", cls: "bg-amber-100 text-amber-800", icon: ShoppingCart },
};
const otype = (t) => t?.order_type || "transfer";

const ORDER_COLS = [
  { key: "transfer_no", label: "No", locked: true },
  { key: "otype", label: "Tür" },
  { key: "route", label: "Güzergah / Tedarikçi" },
  { key: "lines", label: "Kalem" },
  { key: "owner", label: "Oluşturan" },
  { key: "status", label: "Durum", locked: true },
  { key: "created_at", label: "Tarih" },
];

// Sabitler & sunum bileşenleri (TYPE_FILTERS, SHIPMENT_METHODS, SHIP_LABEL, DOC_TYPES,
// docFileHref, DocSummary, StatCard) → components/orders/shared.jsx dosyasına taşındı.

export default function OrdersWorkspace({ scope = "all" }) {
  const { user, isPrivileged, can, feature } = useAuth();
  const [searchParams] = useSearchParams();
  const [mineOnly, setMineOnly] = useState(searchParams.get("mine") === "1");
  const isTransferScope = scope === "transfer";
  const isOrdersScope = scope === "orders";
  const { visible: colVis, density: rowDensity, toggle: toggleCol, setDensity: setColDensity, reset: resetCols } = useTablePrefs("orders_" + scope, ORDER_COLS);
  const colCount = 1 + ORDER_COLS.filter((c) => colVis[c.key]).length;
  const [transfers, setTransfers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [view, setView] = useState("active"); // active | archive
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [newKind, setNewKind] = useState(null); // "transfer" | "internal" | "purchase"
  const [detail, setDetail] = useState(null);
  const [dtab, setDtab] = useState("summary");
  const [mode, setMode] = useState("view");
  const [qtyMap, setQtyMap] = useState({});
  const [actionNote, setActionNote] = useState("");
  const [opPin, setOpPin] = useState("");   // Kimlik Modeli: ortak girişte işlemi yapan personel PIN'i
  const [busy, setBusy] = useState(false);
  const [dispatchOrders, setDispatchOrders] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [supSummaryId, setSupSummaryId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkApprove = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/orders/chain-bulk-approve", { ids: [...selected] });
      toast.success(`${data.approved} onaylandı${data.skipped ? ` · ${data.skipped} atlandı (sıra sizde değil)` : ""}`);
      setSelected(new Set());
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBulkBusy(false);
  };
  const [bulkNotifyBusy, setBulkNotifyBusy] = useState(false);
  const [bulkNotifyOpen, setBulkNotifyOpen] = useState(false);
  const [waItems, setWaItems] = useState(null);
  const bulkNotify = async () => {
    setBulkNotifyBusy(true);
    try {
      const { data } = await api.post("/orders/bulk-notify-supplier", { ids: [...selected] });
      toast.success(`${data.sent} tedarikçiye e-posta gönderildi${data.skipped ? ` · ${data.skipped} atlandı (e-posta yok/başarısız)` : ""}`);
      setBulkNotifyOpen(false); setSelected(new Set());
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBulkNotifyBusy(false);
  };
  const bulkNotifyWhatsapp = async () => {
    setBulkNotifyBusy(true);
    try {
      const { data } = await api.post("/orders/bulk-notify-supplier-whatsapp", { ids: [...selected] });
      setWaItems(data);
      (data.items || []).slice(0, 1).forEach((it) => window.open(it.wa_url, "_blank"));
      toast.success(`${(data.items || []).length} tedarikçi için WhatsApp linki hazır${data.skipped?.length ? ` · ${data.skipped.length} atlandı (telefon yok)` : ""}`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBulkNotifyBusy(false);
  };

  const [form, setForm] = useState({ from_location_id: "", to_location_id: "", store_location_id: "", warehouse_location_id: "", supplier: "", note: "", shipment_method: "", shipment_note: "", lines: [] });
  const [reqDoc, setReqDoc] = useState(null);
  const [reqDocUp, setReqDocUp] = useState(false);
  const [pq, setPq] = useState("");
  const [results, setResults] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supSearch, setSupSearch] = useState("");
  const [supOpen, setSupOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [pcat, setPcat] = useState("");
  const [shipDoc, setShipDoc] = useState({ type: "", no: "", detail: "", files: [] });
  const [shipUploading, setShipUploading] = useState(false);
  const [recvDoc, setRecvDoc] = useState({ type: "", no: "", detail: "", files: [] });
  const [recvUploading, setRecvUploading] = useState(false);

  const myLocIds = user?.location_ids?.length ? user.location_ids : (user?.location_id ? [user.location_id] : []);
  const storeOpts = locations.filter((l) => (l.types || [l.type]).includes("store") && l.is_active !== false && (isPrivileged || myLocIds.includes(l.id)));
  const whOpts = locations.filter((l) => (l.types || [l.type]).includes("warehouse") && l.is_active !== false && (isPrivileged || myLocIds.includes(l.id)));

  const load = useCallback(() => {
    const params = [];
    if (typeFilter) params.push(`type=${typeFilter}`);
    api.get(`/transfers${params.length ? `?${params.join("&")}` : ""}`).then((r) => setTransfers(r.data)).catch(() => {});
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  const { sortKey, sortDir, toggle: onSort, sortRows } = useSort();
  const ACTIVE_ST = ["requested", "approved", "in_transit"];
  const ARCHIVE_ST = ["completed", "rejected", "cancelled"];
  const inScope = (t) => (scope === "all" ? true : isTransferScope ? (otype(t) === "transfer" || otype(t) === "internal_order") : otype(t) === "purchase_order");
  const scopedTypeFilters = isTransferScope ? TYPE_FILTERS.filter((f) => f.v !== "purchase_order") : isOrdersScope ? [] : TYPE_FILTERS;
  const summaryRows = (isTransferScope || isOrdersScope) ? transfers.filter(inScope) : [];
  const summ = {
    total: summaryRows.length,
    pending: summaryRows.filter((t) => t.status === "requested").length,
    in_transit: summaryRows.filter((t) => t.status === "in_transit").length,
    completed: summaryRows.filter((t) => t.status === "completed").length,
    transferN: summaryRows.filter((t) => otype(t) === "transfer").length,
    ikmalN: summaryRows.filter((t) => otype(t) === "internal_order").length,
    openValue: summaryRows.filter((t) => ["requested", "approved", "in_transit"].includes(t.status))
      .reduce((s, t) => s + (t.lines || []).reduce((a, l) => a + (Number(l.qty_requested) || 0) * (Number(l.unit_cost) || 0), 0), 0),
  };
  const awaitsMe = (t) => !!(t.approval_chain?.length && t.status === "requested" && (isPrivileged || (t.approval_chain[t.approval_step || 0]?.roles || []).includes(user?.role)));
  const mineCount = transfers.filter((t) => inScope(t) && awaitsMe(t)).length;
  const shown = sortRows(transfers.filter((t) => inScope(t) && (view === "archive" ? ARCHIVE_ST : ACTIVE_ST).includes(t.status) && (!mineOnly || awaitsMe(t))), {
    transfer_no: (t) => t.transfer_no || "",
    otype: (t) => otype(t),
    route: (t) => t.from_location_name || t.supplier || t.to_location_name || "",
    lines: (t) => t.lines?.length || 0,
    owner: (t) => t.requested_by || "",
    status: (t) => t.status || "",
    created_at: (t) => t.created_at || "",
  });
  useEffect(() => { setPage(1); }, [view, typeFilter, pageSize, transfers.length]);
  const pagedTransfers = shown.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  // ---- product search (shared) ----
  const searchProducts = useCallback(() => {
    if (!newKind) { setResults([]); return; }
    if (newKind === "transfer" && !form.from_location_id) { setResults([]); return; }
    const base = pq ? `q=${encodeURIComponent(pq)}&page_size=30` : `page_size=60`;
    let url = `/products?${base}`;
    if (pcat) url += `&category=${encodeURIComponent(pcat)}`;
    if (newKind === "transfer" && form.from_location_id) url += `&location_id=${form.from_location_id}`;
    // Talep Belgesi (iç ikmal): yalnız "siparişe açık" ürünler + bağlı DEPO stoğu gösterilir.
    if (newKind === "internal") {
      url += `&orderable=true`;
      const internalWh = storeOpts.find((l) => l.id === form.store_location_id)?.linked_warehouse_id;
      if (internalWh) url += `&location_id=${internalWh}`;
    }
    api.get(url).then((r) => {
      let items = r.data.items || [];
      if (pq) {
        const t = pq.toLocaleLowerCase("tr");
        items = [...items].sort((a, b) => {
          const ap = (a.name || "").toLocaleLowerCase("tr").startsWith(t) ? 0 : 1;
          const bp = (b.name || "").toLocaleLowerCase("tr").startsWith(t) ? 0 : 1;
          return ap - bp || (a.name || "").localeCompare(b.name || "", "tr");
        });
      }
      setResults(items);
    }).catch(() => {});
  }, [pq, pcat, newKind, form.from_location_id, form.store_location_id, storeOpts]);
  useEffect(() => { const t = setTimeout(searchProducts, 300); return () => clearTimeout(t); }, [searchProducts]);

  const resetForm = () => { setReqDoc(null); setSupSearch(""); setSupOpen(false); setOpPin(""); setForm({ from_location_id: "", to_location_id: "", store_location_id: "", warehouse_location_id: "", supplier: "", note: "", shipment_method: "", shipment_note: "", lines: [] }); };
  const openNew = (kind) => {
    resetForm();
    setForm((f) => ({
      ...f,
      from_location_id: kind === "transfer" && !isPrivileged && user?.location_id ? user.location_id : "",
      store_location_id: kind === "internal" && storeOpts.length === 1 ? storeOpts[0].id : "",
      warehouse_location_id: kind === "purchase" && whOpts.length === 1 ? whOpts[0].id : "",
    }));
    setPq(""); setPcat(""); setResults([]); setNewKind(kind);
  };

  const addLine = (p) => {
    if (form.lines.find((l) => l.product_id === p.id)) return;
    setForm((f) => ({ ...f, lines: [...f.lines, { product_id: p.id, name: p.name, unit: p.base_unit || "adet", stock: p.stock_on_hand, qty: 1, unit_cost: p.last_cost || 0, vat_rate: p.vat_rate ?? 20 }] }));
    setPq(""); setResults([]);
  };
  const setLine = (pid, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l) => l.product_id === pid ? { ...l, [k]: v } : l) }));
  const rmLine = (pid) => setForm((f) => ({ ...f, lines: f.lines.filter((l) => l.product_id !== pid) }));

  const submitNew = async () => {
    if (form.lines.length === 0) return toast.error("En az bir ürün ekleyin");
    if (user?.is_shared_login && !opPin) return toast.error("Ortak giriş — bu işlemi yapan personelin kendi PIN'i zorunludur");
    if (newKind === "transfer") {
      if (!form.from_location_id || !form.to_location_id) return toast.error("Kaynak ve hedef lokasyon seçin");
      if (form.from_location_id === form.to_location_id) return toast.error("Kaynak ve hedef aynı olamaz");
      if (feature("flow.require_purchase_document", true) && !reqDoc) return toast.error("Transfer için imzalı belge (PDF/PNG/JPEG) yüklemesi zorunludur");
      if (form.note.trim().split(/\s+/).filter(Boolean).length < 1) return toast.error("Not/açıklama zorunludur (en az 1 kelime)");
    }
    if (newKind === "internal" && !isPrivileged && storeOpts.length !== 1 && !form.store_location_id) return toast.error("Mağaza seçin");
    if (newKind === "purchase") {
      if (!form.supplier) return toast.error("Kayıtlı bir tedarikçi seçin");
      if (isPrivileged && !form.warehouse_location_id) return toast.error("Depo seçin");
      if (form.lines.some((l) => !(Number(l.unit_cost) > 0))) return toast.error("Her ürün için alış fiyatı (>0) girin");
    }
    if (newKind === "internal" || newKind === "purchase") {
      if (feature("flow.require_shipment_method", true) && !form.shipment_method) return toast.error("Sevkiyat şekli seçilmeli");
      if (form.note.trim().split(/\s+/).filter(Boolean).length < 1) return toast.error("Açıklama zorunludur (en az 1 kelime)");
      if (feature("flow.require_purchase_document", true) && !reqDoc) return toast.error("Evrak yüklemesi zorunludur (PDF/PNG/JPEG)");
    }
    if (form.shipment_method === "diger" && !form.shipment_note.trim()) return toast.error("Sevkiyat şekli 'Diğer' için açıklama girin");
    const ship = { shipment_method: form.shipment_method || undefined, shipment_note: form.shipment_method === "diger" ? form.shipment_note.trim() : undefined };
    setBusy(true);
    try {
      if (newKind === "transfer") {
        await api.post("/transfers", { from_location_id: form.from_location_id, to_location_id: form.to_location_id, note: form.note, operator_pin: user?.is_shared_login ? opPin : undefined, document: reqDoc, ...ship, lines: form.lines.map((l) => ({ product_id: l.product_id, qty: Number(l.qty) || 0 })) });
        toast.success("Transfer talebi oluşturuldu");
      } else if (newKind === "internal") {
        await api.post("/orders/internal", { store_location_id: form.store_location_id || undefined, note: form.note, operator_pin: user?.is_shared_login ? opPin : undefined, document: reqDoc, ...ship, lines: form.lines.map((l) => ({ product_id: l.product_id, qty: Number(l.qty) || 0 })) });
        toast.success("İç ikmal siparişi oluşturuldu");
      } else if (newKind === "purchase") {
        await api.post("/orders/purchase", { warehouse_location_id: form.warehouse_location_id || undefined, supplier: form.supplier, note: form.note, operator_pin: user?.is_shared_login ? opPin : undefined, document: reqDoc, ...ship, lines: form.lines.map((l) => ({ product_id: l.product_id, qty: Number(l.qty) || 0, unit_cost: Number(l.unit_cost) || 0 })) });
        toast.success("Satın alma siparişi oluşturuldu (onaya gönderildi)");
      }
      setNewKind(null); resetForm(); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ---- Detail actions ----
  const openDetail = async (tr) => {
    setMode("view"); setActionNote(""); setQtyMap({}); setDtab("summary");
    setDetail(tr);
    try { const { data } = await api.get(`/transfers/${tr.id}`); setDetail(data); } catch { /* liste satırı ile aç */ }
  };
  const refreshDetail = async (tid) => { const { data } = await api.get(`/transfers/${tid}`); setDetail(data); load(); };
  const [edocPolicy, setEdocPolicy] = useState(null);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const approvalCredsRef = useRef([]);
  useEffect(() => { api.get("/edoc/issue-policy").then((r) => setEdocPolicy(r.data)).catch(() => {}); }, []);
  const doDespatch = async (approvals) => {
    try {
      const { data } = await api.post(`/e-belge/despatch/${detail.id}`, { approvals });
      toast.success(`e-İrsaliye oluşturuldu: ${data.doc_no}${data.status === "draft" ? " (taslak)" : ""}`);
      return true;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); return false; }
  };
  const issueDespatch = async () => {
    if (edocPolicy && !edocPolicy.can_self_issue) { approvalCredsRef.current = []; setApprovalOpen(true); return; }
    await doDespatch(null);
  };
  const onDespatchApproved = async (cred) => {
    const creds = [...approvalCredsRef.current, cred];
    approvalCredsRef.current = creds;
    setApprovalOpen(false);
    const ok = await doDespatch(creds);
    if (ok) { approvalCredsRef.current = []; }
    else { setTimeout(() => setApprovalOpen(true), 80); }  // yetersizse sıradaki yetkiliyi ekle
  };
  const isOrder = detail && otype(detail) !== "transfer";
  const base = isOrder ? "orders" : "transfers";

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyBusy, setNotifyBusy] = useState(false);
  const notifySupplier = async (channel) => {
    setNotifyBusy(true);
    try {
      const { data } = await api.post(`/orders/${detail.id}/notify-supplier`, { channel });
      if (channel === "whatsapp" && data.wa_url) { window.open(data.wa_url, "_blank"); toast.success("WhatsApp mesajı hazırlandı"); }
      else toast.success(`Tedarikçiye e-posta gönderildi${data.to ? `: ${data.to}` : ""}`);
      setNotifyOpen(false);
      await refreshDetail(detail.id);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setNotifyBusy(false); }
  };

  const doAction = async (path, payload) => {
    setBusy(true);
    try {
      await api.post(`/${base}/${detail.id}/${path}`, payload || {});
      toast.success("İşlem tamam");
      await refreshDetail(detail.id);
      setMode("view");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  // transfer (manual) sides
  const mySides = (tr) => {
    if (!tr) return new Set();
    if (isPrivileged) return new Set(["from", "to"]);
    const s = new Set();
    if (myLocIds.includes(tr.from_location_id)) s.add("from");
    if (myLocIds.includes(tr.to_location_id)) s.add("to");
    return s;
  };
  const sides = mySides(detail);

  // order authority
  const relevantLoc = detail && (otype(detail) === "internal_order" ? detail.from_location_id : detail.to_location_id);
  const orderAuth = detail && (isPrivileged || myLocIds.includes(relevantLoc));

  // İş Akışı Zinciri (çok kademeli onay)
  const chainActive = !!(detail && detail.approval_chain && detail.approval_chain.length);
  const chainStep = detail?.approval_step || 0;
  const chainLevel = chainActive ? detail.approval_chain[chainStep] : null;
  const chainRoles = chainLevel?.roles || [];
  const chainMyTurn = !chainActive || isPrivileged || chainRoles.includes(user?.role);

  const startShip = () => { setMode("ship"); setDtab("lines"); setOpPin(""); setShipDoc({ type: "", no: "", detail: "", files: [] }); setQtyMap(Object.fromEntries(detail.lines.map((l) => [l.product_id, l.qty_requested]))); };
  const startReceive = () => { setMode("receive"); setDtab("lines"); setOpPin(""); setRecvDoc({ type: "", no: "", detail: "", files: [] }); setQtyMap(Object.fromEntries(detail.lines.map((l) => [l.product_id, l.qty_shipped]))); };
  const confirmShip = () => {
    if (!shipDoc.type) return toast.error("Sevk belgesi türü seçin");
    if (shipDoc.type === "diger" && !shipDoc.detail.trim()) return toast.error("Belge açıklaması girin");
    if (shipDoc.files.length === 0) return toast.error("İmzalı sevk belgesi yükleyin");
    if (user?.is_shared_login && !opPin) return toast.error("Ortak giriş — bu işlemi yapan personelin kendi PIN'i zorunludur");
    doAction("ship", {
      lines: detail.lines.map((l) => ({ product_id: l.product_id, qty_shipped: Number(qtyMap[l.product_id]) || 0 })),
      note: actionNote,
      operator_pin: user?.is_shared_login ? opPin : undefined,
      doc_type: shipDoc.type, doc_no: shipDoc.no || undefined,
      doc_detail: shipDoc.type === "diger" ? shipDoc.detail : undefined,
      doc_urls: shipDoc.files.map((d) => d.url),
    });
  };
  const uploadShipDoc = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setShipUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setShipDoc((s) => ({ ...s, files: [...s.files, data] }));
      }
      toast.success("Belge yüklendi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setShipUploading(false); }
  };
  const removeShipDoc = (url) => setShipDoc((s) => ({ ...s, files: s.files.filter((d) => d.url !== url) }));
  const genShipDocNo = async () => {
    try {
      const { data } = await api.get("/logistics/generate-doc-no", { params: { type: "dispatch" } });
      setShipDoc((s) => ({ ...s, no: data.doc_no }));
      toast.success(`Belge No üretildi: ${data.doc_no}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const uploadReqDoc = async (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f) return;
    setReqDocUp(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReqDoc(data); toast.success("Evrak yüklendi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setReqDocUp(false); }
  };
  const confirmReceive = () => {
    if (!recvDoc.type) return toast.error("Teslim belgesi türü seçin");
    if (recvDoc.type === "diger" && !recvDoc.detail.trim()) return toast.error("Belge açıklaması girin");
    if (recvDoc.files.length === 0) return toast.error("İmzalı teslim belgesi yükleyin (belge olmadan teslim alınamaz)");
    if (user?.is_shared_login && !opPin) return toast.error("Ortak giriş — bu işlemi yapan personelin kendi PIN'i zorunludur");
    doAction("receive", {
      lines: detail.lines.map((l) => ({ product_id: l.product_id, qty_received: Number(qtyMap[l.product_id]) || 0 })),
      note: actionNote,
      operator_pin: user?.is_shared_login ? opPin : undefined,
      doc_type: recvDoc.type, doc_no: recvDoc.no || undefined,
      doc_detail: recvDoc.type === "diger" ? recvDoc.detail : undefined,
      doc_urls: recvDoc.files.map((d) => d.url),
    });
  };
  const uploadRecvDoc = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setRecvUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setRecvDoc((s) => ({ ...s, files: [...s.files, data] }));
      }
      toast.success("Belge yüklendi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setRecvUploading(false); }
  };
  const removeRecvDoc = (url) => setRecvDoc((s) => ({ ...s, files: s.files.filter((d) => d.url !== url) }));

  // visibility
  let canApprove = false, canReject = false, canShip = false, canReceiveTransfer = false, canOrderShipDirect = false;
  if (detail) {
    if (isOrder) {
      canApprove = detail.status === "requested" && can("transfer.approve") && orderAuth && chainMyTurn;
      canReject = detail.status === "requested" && can("transfer.approve") && orderAuth;
      const shipReady = detail.status === "approved" && can("transfer.create") && orderAuth;
      canShip = shipReady && otype(detail) === "internal_order"; // qty modu
      canOrderShipDirect = shipReady && otype(detail) === "purchase_order" && feature("flow.purchase_supplier_send", true); // tedarikçiye sipariş ver (opsiyonel akış)
    } else {
      canApprove = detail.status === "requested" && chainMyTurn && (chainActive ? (sides.size > 0 || isPrivileged) : ((sides.has("from") && !detail.from_approved) || (sides.has("to") && !detail.to_approved)));
      canReject = ["requested", "approved"].includes(detail.status) && sides.size > 0;
      canShip = detail.status === "approved" && sides.has("from");
      canReceiveTransfer = detail.status === "in_transit" && sides.has("to");
    }
  }

  const renderLines = (kind) => (
    <>
      <div className="flex items-center gap-2 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        <span className="flex-1">Ürün</span>
        <span className="w-20 text-center">Miktar</span>
        {kind === "purchase" && <span className="w-24 text-center">Alış Fiyatı {curSym()}</span>}
        <span className="w-6" />
      </div>
      {form.lines.map((l) => (
        <div key={l.product_id} className="flex items-end gap-2 rounded-md bg-white p-2 ring-1 ring-inset ring-slate-200">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-800">{l.name}</p>
            {kind === "transfer" && <p className="text-[10px] text-slate-400">stok {l.stock}</p>}
            {kind === "purchase" && <p className="text-[10px] text-slate-400">KDV %{l.vat_rate ?? 20}</p>}
          </div>
          <div className="w-20">
            <label className="mb-0.5 block text-center text-[10px] font-medium text-slate-500">Miktar ({l.unit})</label>
            <Input data-testid={`order-qty-${l.product_id}`} type="number" min="0" value={l.qty}
              onChange={(e) => setLine(l.product_id, "qty", e.target.value)} className="h-9 tabular text-center" />
            {kind === "transfer" && l.stock != null && (
              <button type="button" data-testid={`order-qty-all-${l.product_id}`} onClick={() => setLine(l.product_id, "qty", l.stock)}
                className="mt-1 w-full rounded bg-brand-green/10 px-1 py-0.5 text-[10px] font-semibold text-brand-green hover:bg-brand-green/20">Tümü ({l.stock})</button>
            )}
          </div>
          {kind === "purchase" && (
            <div className="w-24">
              <label className="mb-0.5 block text-center text-[10px] font-medium text-slate-500">Alış {curSym()} / birim</label>
              <PriceInput data-testid={`order-cost-${l.product_id}`} type="number" min="0" step="0.01" value={l.unit_cost}
                onChange={(e) => setLine(l.product_id, "unit_cost", e.target.value)} wrapperClassName="" className="h-9 tabular" placeholder="0,00" />
            </div>
          )}
          <button onClick={() => rmLine(l.product_id)} className="mb-2 shrink-0 text-red-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {kind === "purchase" && (() => {
        const net = form.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_cost) || 0), 0);
        const kdv = form.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_cost) || 0) * ((Number(l.vat_rate ?? 20)) / 100), 0);
        return (
          <>
            <div className="mt-1 space-y-1 border-t border-slate-200 px-1 pt-2 text-sm">
              <div className="flex items-center justify-between text-slate-600"><span>Ara Toplam (KDV hariç)</span><span data-testid="order-subtotal" className="tabular">{fmtTL(net)}</span></div>
              <div className="flex items-center justify-between text-slate-600"><span>KDV</span><span data-testid="order-vat" className="tabular">{fmtTL(kdv)}</span></div>
              <div className="flex items-center justify-between font-semibold text-slate-800"><span>Genel Toplam (KDV dahil)</span><span data-testid="order-total" className="tabular text-brand-green">{fmtTL(net + kdv)}</span></div>
            </div>
            <p className="px-1 text-[11px] leading-snug text-slate-500">
              <b>Miktar</b>: kaç birim alınacağı · <b>Alış {curSym()} / birim</b>: 1 birimin KDV <b>hariç</b> alış fiyatı. Genel Toplam, her ürünün KDV oranı eklenerek hesaplanır.
            </p>
          </>
        );
      })()}
    </>
  );

  const renderRow = (t) => {
    const st = STATUS[t.status] || STATUS.requested;
    const ot = OTYPE[otype(t)] || OTYPE.transfer;
    return (
      <tr key={t.id} data-testid={`transfer-row-${t.id}`} onClick={() => openDetail(t)} className="cursor-pointer hover:bg-slate-50">
        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" data-testid={`tr-select-${t.id}`} checked={selected.has(t.id)} onChange={() => toggleSel(t.id)} className="h-4 w-4 accent-brand-green" />
        </td>
        {colVis.transfer_no && <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-700">{t.transfer_no}</td>}
        {colVis.otype && <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${ot.cls}`}><ot.icon className="h-3 w-3" /> {ot.label}</span></td>}
        {colVis.route && <td className="px-4 py-3 text-slate-600">
          {otype(t) === "purchase_order"
            ? <span className="inline-flex items-center gap-1"><ShoppingCart className="h-3 w-3 text-amber-500" /> {t.supplier} <ArrowLeftRight className="h-3 w-3 text-slate-300" /> {t.to_location_name}</span>
            : <span><span className="text-slate-800">{t.from_location_name}</span> <ArrowLeftRight className="inline h-3 w-3 text-slate-400" /> <span className="text-slate-800">{t.to_location_name}</span></span>}
        </td>}
        {colVis.lines && <td className="px-4 py-3 text-slate-500 tabular">{t.lines?.length || 0}</td>}
        {colVis.owner && <td data-testid={`tr-owner-${t.id}`} className="px-4 py-3 text-xs text-slate-500"><span className="inline-flex items-center gap-1"><User className="h-3 w-3 text-slate-400" /> {t.requested_by || "—"}</span></td>}
        {colVis.status && <td className="px-4 py-3">
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.cls}`}><st.icon className="h-3 w-3" /> {st.label}</span>
          {t.discrepancy && <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600"><AlertTriangle className="h-3 w-3" /> fark</span>}
          {t.correction_open && <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-medium text-red-600"><AlertTriangle className="h-3 w-3" /> düzeltme</span>}
          {t.approval_chain?.length > 0 && t.status === "requested" && (isPrivileged || (t.approval_chain[t.approval_step || 0]?.roles || []).includes(user?.role)) && (
            <span data-testid={`tr-awaits-me-${t.id}`} className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"><Clock className="h-2.5 w-2.5" /> sizi bekliyor</span>
          )}
          {t.approval_chain?.length > 0 && t.status === "requested" && (Date.now() - new Date(t.created_at)) / 3600000 >= 24 && (
            <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-600"><AlertTriangle className="h-2.5 w-2.5" /> gecikti</span>
          )}
        </td>}
        {colVis.created_at && <td className="px-4 py-3 text-xs text-slate-400">{String(t.created_at).slice(0, 10)}</td>}
      </tr>
    );
  };

  return (
    <div className="space-y-4 fade-in" data-testid="transfers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {isOrdersScope ? <ShoppingCart className="h-5 w-5 text-brand-green" /> : <ArrowLeftRight className="h-5 w-5 text-brand-green" />}
          <div><p className="overline">{isOrdersScope ? "Satın Alma & Tedarik" : "Lojistik"}</p><h1 className="font-heading text-2xl font-bold text-slate-900">{isTransferScope ? "Transferler & Siparişler" : isOrdersScope ? "Satın Alma" : "Transfer & Sipariş"}</h1></div>
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.size > 0 && can("transfer.approve") && (
            <Button data-testid="bulk-approve-btn" onClick={bulkApprove} disabled={bulkBusy} className="bg-emerald-600 text-white hover:bg-emerald-700">
              {bulkBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} Toplu Onayla ({selected.size})
            </Button>
          )}
          {isOrdersScope && selected.size > 0 && can("transfer.create") && (
            <Button data-testid="bulk-notify-btn" onClick={() => { setWaItems(null); setBulkNotifyOpen(true); }} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <Send className="mr-1.5 h-4 w-4" /> Toplu Tedarikçiye Bildir ({selected.size})
            </Button>
          )}
          {selected.size > 0 && (
            <Button data-testid="batch-dispatch-btn" onClick={() => setDispatchOrders(transfers.filter((t) => selected.has(t.id)))} variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10">
              <Printer className="mr-1.5 h-4 w-4" /> Toplu Belge ({selected.size})
            </Button>
          )}
          {!isOrdersScope && <Button data-testid="new-internal-order-btn" onClick={() => openNew("internal")} className="bg-sky-600 text-white hover:bg-sky-700"><PackagePlus className="mr-1.5 h-4 w-4" /> Sipariş</Button>}
          {!isTransferScope && can("purchase.read") && <Button data-testid="new-purchase-order-btn" onClick={() => openNew("purchase")} className="bg-brand-orange text-white hover:bg-orange-600"><ShoppingCart className="mr-1.5 h-4 w-4" /> Satın Alma</Button>}
          {!isOrdersScope && <Button data-testid="new-transfer-btn" onClick={() => openNew("transfer")} variant="outline" className="border-slate-300"><Plus className="mr-1.5 h-4 w-4" /> Manuel Transfer</Button>}
          <ColumnToggle testid="orders-table-tools" cols={ORDER_COLS} visible={colVis} onToggle={toggleCol} density={rowDensity} onDensity={setColDensity} onReset={resetCols} />
        </div>
      </div>
      <p className="text-sm text-slate-500">{isTransferScope ? "Manuel transfer (lokasyonlar arası aktarım) + iç ikmal siparişi (mağaza → bağlı depo). Talep → onay → sevk → mal kabul." : isOrdersScope ? "Tedarikçiden satın alma siparişi. Sipariş → onay → tedarikçiye gönder → mal kabul. Mal kabul, \"Mal Kabul\" ekranından yapılır." : "Sipariş → onay → hazırlık/sevk → mal kabul."}</p>

      {isOrdersScope && (
        <div data-testid="po-summary" className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <StatCard label="Toplam Sipariş" value={summ.total} icon={ShoppingCart} cls="text-amber-500" />
          <StatCard label="Onay Bekleyen" value={summ.pending} icon={Clock} cls="text-amber-500" />
          <StatCard label="Yolda / Sevk" value={summ.in_transit} icon={Truck} cls="text-indigo-500" />
          <StatCard label="Tamamlandı" value={summ.completed} icon={PackageCheck} cls="text-emerald-500" />
          {can("purchase.read") && <StatCard label="Açık Sipariş Tutarı" value={fmtTL(summ.openValue)} icon={BarChart3} cls="text-brand-green" />}
        </div>
      )}
      {isTransferScope && (
        <div data-testid="transfer-summary" className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <StatCard label="Manuel Transfer" value={summ.transferN} icon={ArrowLeftRight} cls="text-slate-500" />
          <StatCard label="İç İkmal" value={summ.ikmalN} icon={PackagePlus} cls="text-sky-500" />
          <StatCard label="Onay Bekleyen" value={summ.pending} icon={Clock} cls="text-amber-500" />
          <StatCard label="Yolda / Sevk" value={summ.in_transit} icon={Truck} cls="text-indigo-500" />
          <StatCard label="Tamamlandı" value={summ.completed} icon={PackageCheck} cls="text-emerald-500" />
          <StatCard label="Toplam" value={summ.total} icon={BarChart3} cls="text-brand-green" />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {scopedTypeFilters.map((f) => (
          <button key={f.v || "all"} data-testid={`tr-type-${f.v || "all"}`} onClick={() => setTypeFilter(f.v)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${typeFilter === f.v ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:bg-slate-50"}`}>
            {f.l}
          </button>
        ))}
        {scopedTypeFilters.length > 0 && <span className="mx-1 h-5 w-px self-center bg-slate-200" />}
        <div className="inline-flex rounded-full bg-white p-0.5 ring-1 ring-inset ring-slate-200">
          <button data-testid="tr-view-active" onClick={() => setView("active")}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${view === "active" ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-700"}`}>
            Aktif İşlemler
          </button>
          <button data-testid="tr-view-archive" onClick={() => setView("archive")}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${view === "archive" ? "bg-slate-800 text-white" : "text-slate-500 hover:text-slate-700"}`}>
            <Archive className="h-3.5 w-3.5" /> Arşiv
          </button>
        </div>
        {(mineCount > 0 || mineOnly) && can("transfer.approve") && (
          <button data-testid="tr-mine-toggle" onClick={() => { setMineOnly((v) => !v); setView("active"); }}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${mineOnly ? "bg-amber-500 text-white" : "bg-white text-amber-700 ring-1 ring-inset ring-amber-300 hover:bg-amber-50"}`}>
            <Clock className="h-3.5 w-3.5" /> Sadece sıra bende{mineCount ? ` (${mineCount})` : ""}
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <table data-density={rowDensity} className="data-table w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <th className="w-8 px-3 py-3"></th>
              {colVis.transfer_no && <SortTh label="No" k="transfer_no" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="tr-sort-no" />}
              {colVis.otype && <SortTh label="Tür" k="otype" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="tr-sort-type" />}
              {colVis.route && <SortTh label="Güzergah / Tedarikçi" k="route" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="tr-sort-route" />}
              {colVis.lines && <SortTh label="Kalem" k="lines" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="tr-sort-lines" />}
              {colVis.owner && <SortTh label="Oluşturan" k="owner" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="tr-sort-owner" />}
              {colVis.status && <SortTh label="Durum" k="status" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="tr-sort-status" />}
              {colVis.created_at && <SortTh label="Tarih" k="created_at" sortKey={sortKey} dir={sortDir} onSort={onSort} testid="tr-sort-date" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-10 text-center text-slate-400">{view === "archive" ? "Arşivde kayıt yok." : "Aktif işlem yok."}</td></tr>
            ) : (isTransferScope && !typeFilter ? (() => {
              const out = [];
              ["transfer", "internal_order"].forEach((gk) => {
                const rows = pagedTransfers.filter((t) => otype(t) === gk);
                if (!rows.length) return;
                const g = OTYPE[gk];
                out.push(
                  <tr key={`grp-${gk}`} className="bg-slate-100/70">
                    <td colSpan={colCount} className="px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <span className="inline-flex items-center gap-1.5"><g.icon className="h-3.5 w-3.5" /> {g.label} <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] text-slate-500 ring-1 ring-inset ring-slate-200">{rows.length}</span></span>
                    </td>
                  </tr>
                );
                rows.forEach((t) => out.push(renderRow(t)));
              });
              return out;
            })() : pagedTransfers.map(renderRow))}
          </tbody>
        </table>
      </div>

      {shown.length > 0 && (
        <Pagination page={page} pageSize={pageSize} total={shown.length} onPage={setPage} onPageSize={setPageSize} testid="tr-pagination" />
      )}

      {/* New dialog */}
      <Dialog open={!!newKind} onOpenChange={(o) => !o && setNewKind(null)}>
        <DialogContent data-testid="new-order-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {newKind === "internal" ? "İç İkmal Siparişi" : newKind === "purchase" ? "Satın Alma Siparişi" : "Yeni Transfer"}
            </DialogTitle>
            <DialogDescription>
              {newKind === "internal" ? "Mağaza için sipariş. Bağlı depo otomatik seçilir; onaydan sonra depo sevk eder." :
               newKind === "purchase" ? "Depoya tedarikçiden mal alım siparişi. Onaydan sonra mal kabulü siparişe bağlı yapılır." :
               "Kaynak lokasyondan hedef lokasyona ürün aktarımı."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {newKind === "transfer" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kaynak (gönderen)</Label>
                  <div className="relative mt-1">
                    <select data-testid="transfer-from" value={form.from_location_id} disabled={!isPrivileged && !!user?.location_id}
                      onChange={(e) => setForm({ ...form, from_location_id: e.target.value, lines: [] })}
                      className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green disabled:opacity-60">
                      <option value="">Seçin</option>
                      {locations.filter((l) => l.is_active !== false).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>
                <div>
                  <Label>Hedef (alıcı)</Label>
                  <div className="relative mt-1">
                    <select data-testid="transfer-to" value={form.to_location_id} onChange={(e) => setForm({ ...form, to_location_id: e.target.value })}
                      className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                      <option value="">Seçin</option>
                      {locations.filter((l) => l.is_active !== false && l.id !== form.from_location_id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>
              </div>
            )}

            {newKind === "internal" && (
              <div>
                <Label className="flex items-center gap-1.5"><Store className="h-4 w-4 text-emerald-500" /> Sipariş Veren Mağaza</Label>
                <div className="relative mt-1">
                  <select data-testid="order-store" value={form.store_location_id} onChange={(e) => setForm({ ...form, store_location_id: e.target.value })}
                    className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                    <option value="">Seçin</option>
                    {storeOpts.map((l) => <option key={l.id} value={l.id}>{l.name}{l.linked_warehouse_name ? ` → ${l.linked_warehouse_name}` : " (bağlı depo yok!)"}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>
                {form.store_location_id && !storeOpts.find((l) => l.id === form.store_location_id)?.linked_warehouse_name && (
                  <p className="mt-1 text-[11px] text-red-500">Bu mağazaya bağlı depo tanımlı değil — Lokasyonlar ekranından ekleyin.</p>
                )}
              </div>
            )}

            {newKind === "purchase" && (
              <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1.5"><Warehouse className="h-4 w-4 text-orange-500" /> Depo</Label>
                  <div className="relative mt-1">
                    <select data-testid="order-warehouse" value={form.warehouse_location_id} onChange={(e) => setForm({ ...form, warehouse_location_id: e.target.value })}
                      className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                      <option value="">Seçin</option>
                      {whOpts.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  </div>
                </div>
                <div>
                  <Label>Tedarikçi (kayıtlı)</Label>
                  {form.supplier ? (
                    <div data-testid="order-supplier-selected" className="mt-1 flex items-center justify-between gap-2 rounded-md border border-brand-green/40 bg-brand-green/5 px-3 py-2">
                      <span className="truncate text-sm font-semibold text-slate-800">{form.supplier}{(() => { const sp = suppliers.find((s) => s.name === form.supplier); return sp?.code ? ` (${sp.code})` : ""; })()}</span>
                      <button type="button" data-testid="order-supplier-change" onClick={() => { setForm({ ...form, supplier: "" }); setSupSearch(""); setSupOpen(true); }}
                        className="shrink-0 text-xs font-semibold text-red-500 hover:underline">Değiştir</button>
                    </div>
                  ) : (
                    <div className="relative mt-1">
                      {supOpen && <div className="fixed inset-0 z-40" onClick={() => setSupOpen(false)} />}
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 z-50 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <Input data-testid="order-supplier-search" value={supSearch} onFocus={() => setSupOpen(true)}
                        onChange={(e) => { setSupSearch(e.target.value); setSupOpen(true); }}
                        placeholder="Tedarikçi ara (ad / kod / kategori)..." className="relative z-50 pl-9" />
                      {supOpen && (
                        <div data-testid="order-supplier-results" className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
                          {(() => {
                            const t = supSearch.trim().toLocaleLowerCase("tr");
                            const match = (s) => {
                              if (!t) return true;
                              const name = (s.name || "").toLocaleLowerCase("tr");
                              const code = (s.code || "").toLocaleLowerCase("tr");
                              const cats = (s.supply_categories || []).map((c) => `${c.category} ${c.subcategory || ""}`).join(" ").toLocaleLowerCase("tr");
                              return name.split(/\s+/).some((w) => w.startsWith(t)) || name.includes(t) || code.includes(t) || cats.includes(t);
                            };
                            const res = suppliers.filter(match).slice(0, 50);
                            if (suppliers.length === 0) return <p className="px-3 py-2 text-[11px] text-red-500">Kayıtlı tedarikçi yok. Önce Tedarikçiler sayfasından ekleyin.</p>;
                            if (res.length === 0) return <p className="px-3 py-2 text-xs text-slate-400">Eşleşen tedarikçi yok.</p>;
                            return (<>
                              <div className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-[10px] text-slate-400">
                                <span>{res.length} tedarikçi{t ? " (eşleşen)" : ""}</span>
                                {t && <button type="button" data-testid="order-supplier-showall" onClick={() => setSupSearch("")} className="font-semibold text-brand-green hover:underline">Tümünü listele</button>}
                              </div>
                              {res.map((s) => (
                                <button key={s.id} type="button" data-testid={`order-supplier-opt-${s.id}`} onClick={() => { setForm({ ...form, supplier: s.name }); setSupOpen(false); setSupSearch(""); }}
                                  className="flex w-full items-start justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50">
                                  <span className="min-w-0">
                                    <span className="block truncate text-sm font-semibold text-slate-800">{s.name}</span>
                                    <span className="text-[10px] text-slate-400">{s.code || "—"}{s.province ? ` · ${s.province}` : ""}</span>
                                  </span>
                                  {(s.supply_categories || []).length > 0 && (
                                    <span data-testid={`order-supplier-cat-${s.id}`} className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                                      {s.supply_categories[0].category}{s.supply_categories.length > 1 ? ` +${s.supply_categories.length - 1}` : ""}
                                    </span>
                                  )}
                                </button>
                              ))}
                            </>);
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  {suppliers.length === 0 && <p className="mt-1 text-[11px] text-red-500">Kayıtlı tedarikçi yok. Önce Tedarikçiler sayfasından tedarikçi ekleyin.</p>}
                </div>
              </div>
              {form.supplier && (() => {
                const sp = suppliers.find((s) => s.name === form.supplier);
                if (!sp) return null;
                return (
                  <div data-testid="order-supplier-card" className="rounded-lg border border-orange-200 bg-orange-50/50 p-3 text-xs">
                    <p className="mb-1.5 flex flex-wrap items-center gap-2 font-heading text-sm font-bold text-slate-800">
                      <span>{sp.name}{sp.code ? <span className="ml-1 font-mono text-[10px] text-slate-400">({sp.code})</span> : null}</span>
                      <SupplierScoreBadge supplierId={sp.id} showLabel={false} />
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-600">
                      {sp.contact && <p className="flex items-center gap-1.5"><User className="h-3 w-3 text-slate-400" /> {sp.contact}</p>}
                      {sp.phone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-slate-400" /> {sp.phone}</p>}
                      {sp.email && <p className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 text-slate-400" /> {sp.email}</p>}
                      {sp.tax_no && <p>V/TC: <span className="tabular">{sp.tax_no}</span></p>}
                      {(sp.province || sp.district || sp.address) && <p className="col-span-2 flex items-start gap-1.5"><MapPin className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" /> <span>{[sp.address, sp.district, sp.province].filter(Boolean).join(", ")}</span></p>}
                    </div>
                    {(sp.supply_categories || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {sp.supply_categories.map((c, i) => <span key={i} className="rounded-full bg-white px-2 py-0.5 text-[10px] text-slate-500 ring-1 ring-inset ring-slate-200">{c.category}{c.subcategory ? ` · ${c.subcategory}` : ""}</span>)}
                      </div>
                    )}
                  </div>
                );
              })()}
              </>
            )}

            <div>
              <Label>Ürün Ekle {newKind === "transfer" && !form.from_location_id && <span className="text-[11px] text-slate-400">(önce kaynak seçin)</span>}</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="order-product-search" value={pq} disabled={newKind === "transfer" && !form.from_location_id} onChange={(e) => setPq(e.target.value)} placeholder="Ürün ara veya tıkla — tümü listelenir" className="pl-9" />
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <select data-testid="order-product-category" value={pcat} disabled={newKind === "transfer" && !form.from_location_id}
                  onChange={(e) => setPcat(e.target.value)}
                  className="h-9 flex-1 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green disabled:opacity-50">
                  <option value="">Tüm Kategoriler</option>
                  {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                {pcat && <button type="button" data-testid="order-product-category-clear" onClick={() => setPcat("")} className="rounded-md bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200">Tümünü gör</button>}
              </div>
              {results.length > 0 && (
                <div className="mt-1 max-h-44 overflow-auto rounded-md border border-slate-200">
                  {results.map((p) => (
                    <button key={p.id} data-testid={`order-add-${p.id}`} onClick={() => addLine(p)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50">
                      <span className="truncate">{p.name}</span>
                      <span className="ml-2 flex shrink-0 items-center gap-2 text-xs text-slate-400">
                        {newKind === "purchase" && p.last_cost > 0 && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">önceki alış {curSym()}{p.last_cost}</span>}
                        <span>stok {p.stock_on_hand}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {form.lines.length > 0 && (
              <div className="space-y-2 rounded-md bg-slate-50 p-2">
                {renderLines(newKind)}
              </div>
            )}
            <div>
              <Label className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-slate-500" /> Sevkiyat Şekli {(newKind !== "transfer" && feature("flow.require_shipment_method", true)) ? <span className="text-[11px] font-semibold text-amber-600">*zorunlu</span> : <span className="text-[11px] font-normal text-slate-400">(opsiyonel)</span>}</Label>
              <div className="relative mt-1">
                <select data-testid="order-shipment-method" value={form.shipment_method} onChange={(e) => setForm({ ...form, shipment_method: e.target.value })}
                  className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                  <option value="">{(newKind !== "transfer" && feature("flow.require_shipment_method", true)) ? "Seçin (zorunlu)" : "Seçin (opsiyonel)"}</option>
                  {SHIPMENT_METHODS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              </div>
              {form.shipment_method === "diger" && (
                <Input data-testid="order-shipment-note" value={form.shipment_note} onChange={(e) => setForm({ ...form, shipment_note: e.target.value })} placeholder="Sevkiyat açıklaması (zorunlu)" className="mt-2" />
              )}
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between gap-2">
                <Label className="flex items-center gap-1.5"><FileText className="h-4 w-4 text-slate-500" /> {newKind === "transfer" ? "İmzalı Transfer / Sevk Belgesi" : "Evrak (Fatura / İrsaliye / Teklif)"} {feature("flow.require_purchase_document", true) ? <span className="text-[11px] font-semibold text-amber-600">*zorunlu</span> : <span className="text-[11px] font-normal text-slate-400">(opsiyonel)</span>}</Label>
                <button type="button" data-testid="order-template-print"
                  onClick={async () => { const no = await printBlankTemplate(newKind === "internal" ? "internal" : newKind === "purchase" ? "purchase" : "transfer"); if (no) toast.success(`Belge No üretildi: ${no}`); }}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-green hover:underline">
                  <Printer className="h-3.5 w-3.5" /> Boş Şablon Yazdır
                </button>
              </div>
              <input id="req-doc-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={uploadReqDoc} />
              <div className={`flex items-center gap-2 rounded-md px-2 py-1.5 ring-1 ring-inset ${(reqDoc || !feature("flow.require_purchase_document", true)) ? "bg-slate-50 ring-slate-200" : "bg-amber-50 ring-amber-300"}`}>
                <Button type="button" data-testid="order-doc-upload" onClick={() => document.getElementById("req-doc-file").click()} disabled={reqDocUp} variant="outline" size="sm" className="border-brand-green text-brand-green hover:bg-brand-green/10">
                  {reqDocUp ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} {reqDoc ? "Belgeyi Değiştir" : "Belge Yükle"}
                </Button>
                {reqDoc && <span data-testid="order-doc-name" className="truncate text-xs font-medium text-emerald-700">✓ {reqDoc.filename}</span>}
              </div>
            </div>
            <Input data-testid="order-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={newKind === "transfer" ? "Not (opsiyonel)" : "Açıklama Giriniz..."} />
            {user?.is_shared_login && (
              <div className="rounded-md bg-indigo-50 p-3 ring-1 ring-inset ring-indigo-200">
                <label className="mb-1 block text-xs font-semibold text-indigo-700">Personel Kodu / PIN <span className="text-red-500">*</span></label>
                <Input data-testid="order-operator-pin" type="password" inputMode="numeric" autoComplete="new-password" value={opPin} onChange={(e) => setOpPin(e.target.value.replace(/\D/g, ""))} placeholder="Kendi PIN'iniz ••••" className="tabular tracking-widest" />
                <p className="mt-1 text-[11px] text-indigo-500">Ortak giriş — bu işlemi <b>yapan personelin kendi PIN'i</b> gerekir. Kayıtta gerçek kişi görünür.</p>
              </div>
            )}
            <Button data-testid="order-submit" onClick={submitNew} disabled={busy || (user?.is_shared_login && !opPin)} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {newKind === "transfer" ? "Transfer Talebi Oluştur" : "Siparişi Oluştur"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="transfer-detail-dialog" className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex flex-wrap items-center gap-2">{detail.transfer_no}
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${(OTYPE[otype(detail)] || OTYPE.transfer).cls}`}>{(OTYPE[otype(detail)] || OTYPE.transfer).label}</span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${(STATUS[detail.status] || STATUS.requested).cls}`}>{(STATUS[detail.status] || STATUS.requested).label}</span>
                </DialogTitle>
                <DialogDescription className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />
                  {otype(detail) === "purchase_order" ? `${detail.supplier} → ${detail.to_location_name}` : `${detail.from_location_name} → ${detail.to_location_name}`}
                </DialogDescription>
                <p data-testid="detail-created-by" className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                  <User className="h-3.5 w-3.5 text-brand-green" />
                  <span>Oluşturan: <b className="text-slate-700">{detail.requested_by || "—"}</b></span>
                  <span className="text-slate-300">·</span>
                  <span>{String(detail.history?.[0]?.at || detail.created_at || "").slice(0, 16).replace("T", " ") || "—"}</span>
                  <span className="text-slate-300">·</span>
                  <span>{detail.lines?.length || 0} kalem</span>
                </p>
                {otype(detail) === "purchase_order" && (() => {
                  const sp = suppliers.find((s) => s.name === detail.supplier);
                  return sp ? (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <SupplierScoreBadge supplierId={sp.id} />
                      <Button type="button" size="sm" variant="outline" data-testid="detail-supplier-summary"
                        onClick={() => setSupSummaryId(sp.id)}
                        className="h-7 w-fit border-brand-green text-brand-green hover:bg-brand-green/10">
                        <BarChart3 className="mr-1 h-3.5 w-3.5" /> Tedarikçi Performansı
                      </Button>
                    </div>
                  ) : null;
                })()}
              </DialogHeader>
              <Tabs value={dtab} onValueChange={setDtab} className="flex min-h-0 flex-1 flex-col">
                <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-lg bg-slate-100 p-1" data-testid="order-detail-tabs">
                  <TabsTrigger value="summary" data-testid="order-tab-summary" className="flex-1">Özet</TabsTrigger>
                  <TabsTrigger value="lines" data-testid="order-tab-lines" className="flex-1">Kalemler ({detail.lines?.length || 0})</TabsTrigger>
                  <TabsTrigger value="docs" data-testid="order-tab-docs" className="flex-1">Belgeler</TabsTrigger>
                  <TabsTrigger value="history" data-testid="order-tab-history" className="flex-1">Onay &amp; Geçmiş</TabsTrigger>
                </TabsList>

                <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                  {/* ---- Özet ---- */}
                  <TabsContent value="summary" className="mt-2 space-y-3">
                    <TransferTimeline detail={detail} />
                    {detail.shipment_method && (
                      <div data-testid="detail-shipment" className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                        <Truck className="h-4 w-4 text-brand-green" />
                        <span className="font-semibold">Sevkiyat Şekli:</span>
                        <span>{SHIP_LABEL[detail.shipment_method] || detail.shipment_method}</span>
                        {detail.shipment_note && <span className="text-slate-500">· {detail.shipment_note}</span>}
                      </div>
                    )}
                    {otype(detail) === "purchase_order" && detail.lines.some((l) => l.unit_cost) && (() => {
                      const net = detail.lines.reduce((s, l) => s + (l.qty_requested || 0) * (l.unit_cost || 0), 0);
                      const kdv = detail.lines.reduce((s, l) => s + (l.qty_requested || 0) * (l.unit_cost || 0) * ((l.vat_rate ?? 20) / 100), 0);
                      return (
                        <div className="space-y-1 rounded-md bg-slate-50 p-2 text-xs">
                          <div className="flex justify-between text-slate-600"><span>Ara Toplam (KDV hariç)</span><span className="tabular">{fmtTL(net)}</span></div>
                          <div className="flex justify-between text-slate-600"><span>KDV</span><span className="tabular">{fmtTL(kdv)}</span></div>
                          <div className="flex justify-between font-semibold text-slate-800"><span>Genel Toplam (KDV dahil)</span><span data-testid="detail-order-total" className="tabular text-brand-green">{fmtTL(net + kdv)}</span></div>
                        </div>
                      );
                    })()}
                    {detail.discrepancy && (
                      <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-700">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Eksik teslim (fark) tespit edildi. {detail.discrepancy_note}
                      </div>
                    )}
                    {detail.correction_open && detail.correction && (
                      <div className="flex items-start gap-2 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid="detail-correction">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Düzeltme talebi: {detail.correction.note} <span className="text-red-400">({detail.correction.by})</span>
                      </div>
                    )}
                    {isOrder && detail.status === "in_transit" && (
                      <p className="rounded-md bg-sky-50 p-2 text-xs text-sky-700">Bu sipariş sevk edildi. Mal kabulünü <b>Mal Kabul</b> ekranından yapın.</p>
                    )}
                  </TabsContent>

                  {/* ---- Kalemler ---- */}
                  <TabsContent value="lines" className="mt-2 space-y-3">
                    <div className="rounded-md ring-1 ring-inset ring-slate-200">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-left text-[11px] text-slate-500">
                          <tr><th className="px-3 py-2">Ürün</th><th className="px-3 py-2 text-right">Talep</th><th className="px-3 py-2 text-right">Depo Stok</th><th className="px-3 py-2 text-right">Sevk</th><th className="px-3 py-2 text-right">Teslim</th><th className="px-3 py-2 text-right">Karşılama</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {detail.lines.map((l) => (
                            <tr key={l.product_id}>
                              <td className="px-3 py-2">{l.name}{l.unit_cost ? <span className="ml-1 text-[10px] text-slate-400">· {curSym()}{l.unit_cost}</span> : null}</td>
                              <td className="px-3 py-2 text-right tabular">{l.qty_requested}</td>
                              <td className={`px-3 py-2 text-right tabular ${(l.info?.current_stock != null && Number(l.info.current_stock) < Number(l.qty_requested)) ? "font-semibold text-red-600" : "text-slate-600"}`} data-testid={`ship-stock-${l.product_id}`}>
                                {l.info?.current_stock != null ? l.info.current_stock : "—"}
                              </td>
                              <td className="px-3 py-2 text-right tabular">
                                {mode === "ship" ? (
                                  <span className="inline-flex items-center justify-end gap-1">
                                    <Input data-testid={`ship-qty-${l.product_id}`} type="number" min="0" max={l.qty_requested} value={qtyMap[l.product_id] ?? ""} onChange={(e) => setQtyMap((m) => ({ ...m, [l.product_id]: e.target.value }))} className="h-7 w-16 tabular" />
                                    <button type="button" data-testid={`ship-qty-stock-${l.product_id}`} title="Depodaki stok kadar" onClick={() => setQtyMap((m) => ({ ...m, [l.product_id]: Math.min(Number(l.qty_requested) || 0, l.info?.current_stock != null ? Number(l.info.current_stock) : (Number(l.qty_requested) || 0)) }))} className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-200">Stok</button>
                                    <button type="button" data-testid={`ship-qty-all-${l.product_id}`} onClick={() => setQtyMap((m) => ({ ...m, [l.product_id]: l.qty_requested }))} className="rounded bg-brand-green/10 px-1.5 py-0.5 text-[10px] font-semibold text-brand-green hover:bg-brand-green/20">Tümü</button>
                                  </span>
                                ) : (l.qty_shipped || "—")}
                              </td>
                              <td className="px-3 py-2 text-right tabular">{l.qty_received || "—"}</td>
                              <td className="px-3 py-2 text-right">
                                {(() => {
                                  const req = Number(l.qty_requested) || 0;
                                  const shipped = mode === "ship" ? (Number(qtyMap[l.product_id]) || 0) : (l.qty_shipped != null ? Number(l.qty_shipped) : null);
                                  if (!req || shipped == null) return <span className="text-slate-300">—</span>;
                                  const pct = Math.round((shipped / req) * 100);
                                  const tone = pct >= 100 ? "bg-emerald-100 text-emerald-700" : (pct <= 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700");
                                  return <span data-testid={`fulfill-pct-${l.product_id}`} className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-bold tabular ${tone}`}>%{pct}</span>;
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {detail.lines?.some((l) => l.info) && (
                      <div className="rounded-md ring-1 ring-inset ring-slate-200" data-testid="order-analysis">
                        <div className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          <BarChart3 className="h-3.5 w-3.5 text-brand-green" /> Onay İçin Ürün Analizi
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="text-left text-[10px] text-slate-400">
                              <tr>
                                <th className="px-3 py-1.5">Ürün</th>
                                <th className="px-3 py-1.5 text-right">Mevcut Stok</th>
                                {detail.can_cost && <th className="px-3 py-1.5 text-right">Önceki Alış</th>}
                                {detail.can_price && <th className="px-3 py-1.5 text-right">Piyasa</th>}
                                {detail.can_price && <th className="px-3 py-1.5 text-right">Bizim Satış</th>}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {detail.lines.map((l) => {
                                const i = l.info || {};
                                return (
                                  <tr key={l.product_id} data-testid={`order-analysis-row-${l.product_id}`}>
                                    <td className="px-3 py-1.5 text-slate-700">{l.name}</td>
                                    <td className="px-3 py-1.5 text-right tabular text-slate-700">
                                      {i.current_stock != null ? i.current_stock : "—"}
                                      <span className="text-slate-400"> / {i.total_stock != null ? i.total_stock : "—"} {i.unit || ""}</span>
                                    </td>
                                    {detail.can_cost && <td className="px-3 py-1.5 text-right tabular text-slate-600">{i.last_cost ? fmtTL(i.last_cost) : "—"}</td>}
                                    {detail.can_price && <td className="px-3 py-1.5 text-right tabular text-slate-600">{i.market_avg ? fmtTL(i.market_avg) : "—"}</td>}
                                    {detail.can_price && <td className="px-3 py-1.5 text-right tabular font-semibold text-brand-green">{i.sales_market_retail ? fmtTL(i.sales_market_retail) : "—"}</td>}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {detail.lines?.[0]?.info?.stock_location_name && (
                          <p className="px-3 py-1.5 text-[10px] text-slate-400">Mevcut stok referans lokasyonu: {detail.lines[0].info.stock_location_name}</p>
                        )}
                      </div>
                    )}

                    {mode === "ship" && (
                      <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/70 p-3">
                        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><FileText className="h-3.5 w-3.5" /> Sevk Belgesi (zorunlu)</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="mb-0.5 block text-[11px] font-medium text-slate-500">Belge Türü</label>
                            <select data-testid="ship-doc-type" value={shipDoc.type} onChange={(e) => setShipDoc({ ...shipDoc, type: e.target.value })}
                              className="h-9 w-full appearance-none rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                              <option value="">Seçin</option>
                              {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="mb-0.5 block text-[11px] font-medium text-slate-500">{shipDoc.type === "diger" ? "Açıklama" : "Belge No (boş = otomatik)"}</label>
                            {shipDoc.type === "diger" ? (
                              <Input data-testid="ship-doc-detail" value={shipDoc.detail} onChange={(e) => setShipDoc({ ...shipDoc, detail: e.target.value })} placeholder="Belge açıklaması" className="h-9" />
                            ) : (
                              <div className="flex gap-1">
                                <Input data-testid="ship-doc-no" value={shipDoc.no} onChange={(e) => setShipDoc({ ...shipDoc, no: e.target.value })} placeholder="Boş bırak → sistem üretir" className="h-9" />
                                <Button type="button" data-testid="ship-doc-gen" onClick={genShipDocNo} variant="outline" size="sm" className="h-9 shrink-0 whitespace-nowrap border-brand-green px-2 text-brand-green hover:bg-brand-green/10" title="Otomatik belge no üret"><Hash className="mr-1 h-3.5 w-3.5" /> Üret</Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <input id="ship-doc-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" multiple className="hidden" onChange={uploadShipDoc} />
                          <Button type="button" data-testid="ship-doc-upload" onClick={() => document.getElementById("ship-doc-file").click()} disabled={shipUploading} variant="outline" size="sm" className="border-brand-green text-brand-green hover:bg-brand-green/10">
                            {shipUploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} İmzalı Belge Yükle
                          </Button>
                          {shipDoc.files.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {shipDoc.files.map((d) => (
                                <span key={d.url} data-testid="ship-doc-chip" className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-[11px] text-slate-600 ring-1 ring-inset ring-slate-200">
                                  <FileText className="h-3 w-3 text-brand-green" /> {d.filename || d.ext}
                                  <button onClick={() => removeShipDoc(d.url)} className="text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {mode === "receive" && (
                      <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/70 p-3">
                        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><FileText className="h-3.5 w-3.5" /> Teslim Belgesi (zorunlu)</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="mb-0.5 block text-[11px] font-medium text-slate-500">Belge Türü</label>
                            <select data-testid="recv-doc-type" value={recvDoc.type} onChange={(e) => setRecvDoc({ ...recvDoc, type: e.target.value })}
                              className="h-9 w-full appearance-none rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                              <option value="">Seçin</option>
                              {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="mb-0.5 block text-[11px] font-medium text-slate-500">{recvDoc.type === "diger" ? "Açıklama" : "Belge No (boş = otomatik)"}</label>
                            {recvDoc.type === "diger" ? (
                              <Input data-testid="recv-doc-detail" value={recvDoc.detail} onChange={(e) => setRecvDoc({ ...recvDoc, detail: e.target.value })} placeholder="Belge açıklaması" className="h-9" />
                            ) : (
                              <Input data-testid="recv-doc-no" value={recvDoc.no} onChange={(e) => setRecvDoc({ ...recvDoc, no: e.target.value })} placeholder="Boş bırak → sistem üretir" className="h-9" />
                            )}
                          </div>
                        </div>
                        <div>
                          <input id="recv-doc-file" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" multiple className="hidden" onChange={uploadRecvDoc} />
                          <Button type="button" data-testid="recv-doc-upload" onClick={() => document.getElementById("recv-doc-file").click()} disabled={recvUploading} variant="outline" size="sm" className="border-brand-green text-brand-green hover:bg-brand-green/10">
                            {recvUploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} İmzalı Belge Yükle
                          </Button>
                          {recvDoc.files.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {recvDoc.files.map((d) => (
                                <span key={d.url} data-testid="recv-doc-chip" className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-[11px] text-slate-600 ring-1 ring-inset ring-slate-200">
                                  <FileText className="h-3 w-3 text-brand-green" /> {d.filename || d.ext}
                                  <button onClick={() => removeRecvDoc(d.url)} className="text-red-400 hover:text-red-600"><X className="h-3 w-3" /></button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {(mode === "ship" || mode === "receive") && (
                      <div className="space-y-2">
                        <Input data-testid="action-note" value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder={mode === "receive" ? "Eksik/fark notu (varsa)" : "Sevk notu (opsiyonel)"} />
                        {user?.is_shared_login && (
                          <div className="rounded-md bg-indigo-50 p-2.5 ring-1 ring-inset ring-indigo-200">
                            <label className="mb-1 block text-xs font-semibold text-indigo-700">Personel Kodu / PIN <span className="text-red-500">*</span></label>
                            <Input data-testid="action-operator-pin" type="password" inputMode="numeric" autoComplete="new-password" value={opPin} onChange={(e) => setOpPin(e.target.value.replace(/\D/g, ""))} placeholder="Kendi PIN'iniz ••••" className="tabular tracking-widest" />
                            <p className="mt-1 text-[11px] text-indigo-500">Ortak giriş — bu işlemi <b>yapan personelin kendi PIN'i</b> gerekir.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  {/* ---- Belgeler ---- */}
                  <TabsContent value="docs" className="mt-2 space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <Button data-testid="dispatch-print-btn" onClick={() => setDispatchOrders([detail])} variant="outline" size="sm" className="border-slate-300">
                        <Printer className="mr-1.5 h-4 w-4" /> Sevkiyat Belgesi (Yazdır / PDF / WhatsApp)
                      </Button>
                      <Button data-testid="edespatch-btn" onClick={issueDespatch} variant="outline" size="sm" className="border-brand-green text-brand-green hover:bg-brand-green/10">
                        <FileText className="mr-1.5 h-4 w-4" /> e-İrsaliye Kes
                      </Button>
                      <ApprovalDialog open={approvalOpen} onClose={() => setApprovalOpen(false)} permission="edoc.issue"
                        title="e-İrsaliye Onayı"
                        description={edocPolicy?.peer_pool_total > 0
                          ? `En az ${edocPolicy.peer_need} adet 5★ yetkili onayı ya da bir Yönetici (PIN/kart/şifre). ${approvalCredsRef.current.length} onay alındı.`
                          : "Yönetici onayı gerekli (PIN/kart/şifre)."}
                        onApproved={onDespatchApproved} />
                    </div>
                    {(detail.ship_document || detail.receipt_document) ? (
                      <div className="space-y-2">
                        <DocSummary doc={detail.ship_document} label="Sevk Belgesi" />
                        <DocSummary doc={detail.receipt_document} label="Mal Kabul Belgesi" />
                      </div>
                    ) : (
                      <p className="rounded-md bg-slate-50 p-3 text-xs text-slate-400">Henüz yüklenmiş sevk / teslim belgesi yok.</p>
                    )}
                    {otype(detail) === "purchase_order" && (detail.history || []).some((h) => h.action === "supplier_notified") && (
                      <div data-testid="supplier-notify-timeline" className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700"><Send className="h-3.5 w-3.5" /> Tedarikçi Bildirim Geçmişi</p>
                        <ol className="space-y-1.5">
                          {(detail.history || []).filter((h) => h.action === "supplier_notified").slice().reverse().map((h, i) => {
                            const isWa = /whatsapp/i.test(h.note || "");
                            return (
                              <li key={i} data-testid={`supplier-notify-event-${i}`} className="flex flex-wrap items-center gap-2 text-xs">
                                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isWa ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
                                  {isWa ? <MessageCircle className="h-3 w-3" /> : <Mail className="h-3 w-3" />} {isWa ? "WhatsApp" : "E-posta"}
                                </span>
                                <span className="text-slate-600">{h.note}</span>
                                <span className="ml-auto text-slate-400">{String(h.at || "").slice(0, 16).replace("T", " ")} · {h.by}</span>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    )}
                  </TabsContent>

                  {/* ---- Onay & Geçmiş ---- */}
                  <TabsContent value="history" className="mt-2 space-y-3">
                    {chainActive && (
                      <div data-testid="approval-chain" className="rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700"><CheckCircle2 className="h-3.5 w-3.5" /> Onay Zinciri (Çok Kademeli){(() => { const h = (Date.now() - new Date(detail.created_at)) / 3600000; return detail.status === "requested" && h >= 24 ? <span data-testid="chain-overdue" className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-600"><AlertTriangle className="h-2.5 w-2.5" /> {Math.floor(h / 24)}g gecikti</span> : null; })()}</p>
                          {detail.approval_amount != null && (
                            <span data-testid="chain-amount" className="text-[11px] text-amber-700">Tutar: <b>{fmtTL(detail.approval_amount)}</b>{detail.approval_threshold != null ? ` · Eşik: ${fmtTL(detail.approval_threshold)}` : ""}</span>
                          )}
                        </div>
                        <ol className="space-y-1.5">
                          {detail.approval_chain.map((lv, i) => {
                            const done = i < chainStep;
                            const current = i === chainStep && detail.status === "requested";
                            const logEntry = (detail.approval_log || []).find((e) => e.level === i + 1);
                            return (
                              <li key={i} data-testid={`chain-level-${i}`} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs ${done ? "bg-emerald-100 text-emerald-800" : current ? "bg-white text-amber-800 ring-1 ring-amber-300" : "bg-white/50 text-slate-500"}`}>
                                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${done ? "bg-emerald-500 text-white" : current ? "bg-amber-500 text-white" : "bg-slate-200 text-slate-500"}`}>{done ? "✓" : i + 1}</span>
                                <span className="flex-1 font-medium">{lv.label}</span>
                                {done && logEntry ? <span className="text-[10px] text-emerald-600">{logEntry.by}</span> : current ? <span className="text-[10px] font-semibold text-amber-600">Bekliyor</span> : <span className="text-[10px] text-slate-400">Sırada</span>}
                              </li>
                            );
                          })}
                        </ol>
                        {detail.status === "requested" && !chainMyTurn && (
                          <p className="mt-2 text-[11px] text-amber-600">Bu aşamada onay yetkisi: <b>{chainLevel?.label || "Yönetici"}</b>. Sıra sizde değil.</p>
                        )}
                        {detail.status === "approved" && <p className="mt-2 text-[11px] font-semibold text-emerald-600">✓ Zincir tamamlandı — sipariş onaylandı.</p>}
                      </div>
                    )}
                    <AuditTrailPanel detail={detail} />
                    {detail.history?.length > 0 && (
                      <div className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-500">
                        <p className="mb-1 flex items-center gap-1 font-semibold uppercase text-slate-400"><History className="h-3 w-3" /> İşlem Geçmişi</p>
                        {detail.history.map((h, i) => (
                          <p key={i} className="tabular">{String(h.at).slice(0, 16).replace("T", " ")} · <b>{h.action}</b> · {h.by}{h.note ? ` · ${h.note}` : ""}</p>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </div>
              </Tabs>

              {/* Kalıcı işlem çubuğu (sekmeden bağımsız) */}
              <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
                {mode === "view" && canApprove && <Button data-testid="order-approve-btn" onClick={() => doAction("approve", { note: "" })} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700">Onayla</Button>}
                {mode === "view" && canReject && <Button data-testid="order-reject-btn" onClick={() => doAction("reject", { note: "" })} disabled={busy} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">Reddet</Button>}
                {mode === "view" && canOrderShipDirect && <Button data-testid="order-ship-direct-btn" onClick={() => doAction("ship", {})} disabled={busy} className="bg-indigo-600 text-white hover:bg-indigo-700"><ShoppingCart className="mr-1.5 h-4 w-4" /> Tedarikçiye Sipariş Ver</Button>}
                {mode === "view" && detail && otype(detail) === "purchase_order" && ["approved", "in_transit"].includes(detail.status) && can("transfer.create") && <Button data-testid="order-notify-supplier-btn" onClick={() => setNotifyOpen(true)} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><Send className="mr-1.5 h-4 w-4" /> Tedarikçiye Bildir (WP/Mail)</Button>}
                {mode === "view" && canShip && <Button data-testid="order-ship-btn" onClick={startShip} className="bg-indigo-600 text-white hover:bg-indigo-700"><Truck className="mr-1.5 h-4 w-4" /> Hazırla & Sevk Et</Button>}
                {mode === "view" && canReceiveTransfer && <Button data-testid="transfer-receive-btn" onClick={startReceive} className="bg-emerald-600 text-white hover:bg-emerald-700"><PackageCheck className="mr-1.5 h-4 w-4" /> Teslim Al</Button>}
                {mode === "ship" && <><Button variant="outline" onClick={() => setMode("view")}>Vazgeç</Button><Button data-testid="confirm-ship-btn" onClick={confirmShip} disabled={busy} className="bg-indigo-600 text-white hover:bg-indigo-700">Sevki Onayla</Button></>}
                {mode === "receive" && <><Button variant="outline" onClick={() => setMode("view")}>Vazgeç</Button><Button data-testid="confirm-receive-btn" onClick={confirmReceive} disabled={busy} className="bg-emerald-600 text-white hover:bg-emerald-700">Teslimatı Onayla</Button></>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DispatchDialog orders={dispatchOrders} open={!!dispatchOrders} onClose={() => setDispatchOrders(null)} />
      <SupplierSummary supplierId={supSummaryId} onClose={() => setSupSummaryId(null)} />
      <Dialog open={notifyOpen} onOpenChange={setNotifyOpen}>
        <DialogContent className="max-w-md" data-testid="order-notify-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Tedarikçiye Bildir</DialogTitle>
            <DialogDescription>{detail?.transfer_no} nolu sipariş, tedarikçinin sistemde kayıtlı iletişim bilgilerine gönderilir. Ürünler, miktarlar, alış fiyatları ve sipariş PDF'i otomatik eklenir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 p-2.5 text-xs text-slate-600 ring-1 ring-inset ring-slate-200">
              <p className="flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-slate-400" /> {detail?.supplier_info?.name || detail?.supplier || "—"}</p>
              <p className="flex items-center gap-1.5" data-testid="order-notify-phone"><Phone className="h-3.5 w-3.5 text-slate-400" /> {detail?.supplier_info?.phone || "Telefon kayıtlı değil"}</p>
              <p className="flex items-center gap-1.5" data-testid="order-notify-email"><Mail className="h-3.5 w-3.5 text-slate-400" /> {detail?.supplier_info?.email || "E-posta kayıtlı değil"}</p>
            </div>
            <Button data-testid="order-notify-whatsapp" onClick={() => notifySupplier("whatsapp")} disabled={notifyBusy || !detail?.supplier_info?.phone} className="w-full bg-emerald-600 text-white hover:bg-emerald-700"><MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp ile Gönder</Button>
            <Button data-testid="order-notify-email-send" onClick={() => notifySupplier("email")} disabled={notifyBusy || !detail?.supplier_info?.email} className="w-full bg-brand-green text-white hover:bg-brand-green-600"><Mail className="mr-1.5 h-4 w-4" /> E-posta ile Gönder</Button>
            {(!detail?.supplier_info?.phone || !detail?.supplier_info?.email) && <p className="text-[11px] text-amber-600">Eksik iletişim bilgisi için Tedarikçiler sayfasından telefon/e-posta ekleyin.</p>}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkNotifyOpen} onOpenChange={(o) => { setBulkNotifyOpen(o); if (!o) setWaItems(null); }}>
        <DialogContent className="max-w-md" data-testid="bulk-notify-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Toplu Tedarikçiye Bildir</DialogTitle>
            <DialogDescription>{selected.size} seçili satın alma siparişi tedarikçilerine bildirilecek. E-posta topluca gönderilir; WhatsApp her tedarikçi için ayrı sekmede açılır.</DialogDescription>
          </DialogHeader>
          {!waItems ? (
            <div className="space-y-2">
              <Button data-testid="bulk-notify-email" onClick={bulkNotify} disabled={bulkNotifyBusy} className="w-full bg-brand-green text-white hover:bg-brand-green-600">{bulkNotifyBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} Toplu E-posta Gönder</Button>
              <Button data-testid="bulk-notify-whatsapp" onClick={bulkNotifyWhatsapp} disabled={bulkNotifyBusy} className="w-full bg-emerald-600 text-white hover:bg-emerald-700">{bulkNotifyBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-1.5 h-4 w-4" />} Toplu WhatsApp Aç</Button>
            </div>
          ) : (
            <div className="max-h-72 space-y-2 overflow-y-auto">
              <p className="text-xs text-slate-500">Her tedarikçi için WhatsApp'ta açmak üzere tıklayın (tarayıcı çoklu sekmeyi engellerse tek tek açın):</p>
              {(waItems.items || []).map((it, i) => (
                <a key={i} data-testid={`bulk-wa-open-${i}`} href={it.wa_url} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 rounded-md bg-emerald-50 px-3 py-2 text-xs ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100">
                  <span className="font-semibold text-emerald-800">{it.transfer_no} · {it.supplier}</span>
                  <span className="inline-flex items-center gap-1 text-emerald-700"><MessageCircle className="h-3.5 w-3.5" /> Aç</span>
                </a>
              ))}
              {(waItems.skipped || []).map((sk, i) => (
                <div key={`sk-${i}`} className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-400">{sk.transfer_no} — atlandı ({sk.reason})</div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
